import type { InstancedMesh, Object3D } from 'three/webgpu';

/**
 * three r170's `InstanceNode.setup()` picks its instancing strategy from
 * `InstancedMesh.count`: at 1000 or fewer it binds the instance attribute's
 * *entire backing array* as a uniform buffer, and above that it falls back to
 * instanced vertex attributes. A mesh allocated for more than 1024 matrices
 * therefore asks for a UBO larger than WebGPU's 64 KB binding limit, the bind
 * group is rejected, and the mesh draws nothing — no exception, just an empty
 * frame and a console warning from the backend.
 *
 * `setup()` runs once when the material is first compiled, so a mesh that is
 * empty on its first frame latches the broken path and never recovers.
 */
const UBO_INSTANCE_THRESHOLD = 1000;

/**
 * Publishes `used` as the mesh's draw count, padding past the threshold above
 * with zero-scaled instances when necessary. Collapsed instances rasterise no
 * fragments, so the padding costs a bounded number of matrix writes and
 * nothing on the GPU.
 */
export function setInstanceCount(mesh: InstancedMesh, used: number, scratch: Object3D): void {
  // A mesh allocated at or below the threshold is safe on the uniform path.
  const floor = Math.min(UBO_INSTANCE_THRESHOLD + 1, mesh.instanceMatrix.count);

  if (used < floor) {
    scratch.position.set(0, 0, 0);
    scratch.rotation.set(0, 0, 0);
    scratch.scale.setScalar(0);
    scratch.updateMatrix();
    for (let i = used; i < floor; i++) mesh.setMatrixAt(i, scratch.matrix);
  }

  mesh.count = Math.max(used, floor);
}
